import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import { Send as SendIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import apiClient from '../services/api';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const { isAuthenticated } = useAuthStore();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      setSnackbar({
        open: true,
        message: 'Отправлять предложения могут только авторизованные пользователи',
        severity: 'error',
      });
      return;
    }

    if (!subject.trim()) {
      setSnackbar({ open: true, message: 'Укажите тему предложения', severity: 'error' });
      return;
    }

    if (!description.trim()) {
      setSnackbar({ open: true, message: 'Укажите описание предложения', severity: 'error' });
      return;
    }

    if (description.length > 5000) {
      setSnackbar({ open: true, message: 'Описание не должно превышать 5000 символов', severity: 'error' });
      return;
    }

    try {
      setSending(true);
      await apiClient.post('/api/feedback/submit', {
        subject: subject.trim(),
        description: description.trim(),
      });

      setSnackbar({ open: true, message: 'Предложение успешно отправлено!', severity: 'success' });
      setSubject('');
      setDescription('');
      onClose();
    } catch (err: any) {
      console.error('Ошибка отправки:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Ошибка при отправке предложения',
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const handleCancel = () => {
    setSubject('');
    setDescription('');
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ px: { xs: 2, sm: 3 } }}>
          💡 Предложения по развитию продукта
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            label="Тема"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            sx={{ mb: 3 }}
            placeholder="Кратко опишите суть предложения"
          />

          <TextField
            fullWidth
            multiline
            rows={6}
            label="Описание"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 5000 }}
            helperText={`${description.length} / 5000 символов`}
            placeholder="Подробно опишите ваше предложение..."
          />
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={sending}
          >
            Отменить
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSubmit}
            disabled={sending || !subject.trim() || !description.trim()}
          >
            {sending ? 'Отправка...' : 'Отправить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackDialog;
